import api from '../api';
import { EMPTY_CANDIDATE_FORM } from './candidateFormValidation';
import {
  normalizePreferredWorkLocations,
  preferredWorkLocationsToLegacyLocation
} from './preferredWorkLocations';
import { normalizeLanguagesKnown } from './candidateLanguages';
import { normalizeProfessionalRows } from './candidateProfileUtils';
import { normalizeIndianMobileDigits } from '../shared/phoneUtils';

/** Fields persisted on candidate profile — excludes UI-only blobs from save. */
const PERSISTED_KEYS = [
  'personal',
  'educational',
  'professional',
  'contact',
  'skills',
  'technicalSkills',
  'currentSalary',
  'expectedSalary',
  'experienceFrom',
  'experienceTo',
  'experienceYears',
  'experience',
  'location',
  'preferredWorkLocations',
  'status',
  'availability',
  'preferredWorkingModes',
  'contactChannels',
  'isFresher',
  'resumeLink',
  'resumeText',
  'profileHeading',
  'photo',
  'tags',
  'salaryExpectation',
  'languagesKnown',
  'areasOfExpertise'
];

/** API-safe body (no UI-only file blobs). */
export function toCandidateApiBody(form, { includePassword = false } = {}) {
  const experienceYears =
    form.experience !== '' && form.experience != null && Number.isFinite(Number(form.experience))
      ? Number(form.experience)
      : form.experienceYears;

  const body = {
    personal: {
      ...(form.personal || {}),
      name: (form.name || form.personal?.name || '').trim()
    },
    educational: form.educational || [],
    professional: normalizeProfessionalRows(form.professional || []),
    contact: {
      ...(form.contact || {}),
      email: (form.email || form.contact?.email || '').trim(),
      phone: normalizeIndianMobileDigits(form.phone || form.contact?.phone || '')
    },
    skills: form.skills || [],
    technicalSkills: form.technicalSkills || [],
    currentSalary: form.currentSalary ?? '',
    expectedSalary: form.expectedSalary ?? '',
    experience: form.experience ?? '',
    experienceYears,
    preferredWorkLocations: normalizePreferredWorkLocations(form),
    location:
      preferredWorkLocationsToLegacyLocation(form.preferredWorkLocations) ||
      form.location ||
      '',
    availability: form.availability || '',
    preferredWorkingModes: form.preferredWorkingModes || [],
    contactChannels: form.contactChannels || [],
    isFresher: !!form.isFresher,
    resumeLink: form.resumeLink || '',
    resumeText: form.resumeText || '',
    profileHeading: form.profileHeading || '',
    status: form.status || 'active',
    photo: form.photo || '',
    languagesKnown: normalizeLanguagesKnown(form.languagesKnown),
    areasOfExpertise: Array.isArray(form.areasOfExpertise)
      ? [...new Set(form.areasOfExpertise.map(s => String(s).trim()).filter(Boolean))]
      : []
  };

  if (includePassword && form.password) {
    body.password = form.password;
    body.name = body.personal.name;
    body.email = body.contact.email;
    body.phone = body.contact.phone;
  }

  return body;
}

export function buildCandidateSavePayload(form, { requireId = true } = {}) {
  if (!form) return null;
  if (requireId && !form.id) return null;
  const apiBody = toCandidateApiBody(form);
  const payload = {};
  if (form.id) payload.id = form.id;
  for (const key of PERSISTED_KEYS) {
    const val = apiBody[key] !== undefined ? apiBody[key] : form[key];
    if (val !== undefined) payload[key] = val;
  }
  if (form.personal) {
    payload.personal = {
      ...form.personal,
      name: (form.personal.name || form.name || '').trim()
    };
  }
  if (form.contact) {
    payload.contact = { ...form.contact };
  }
  return payload;
}

/** Save logged-in candidate profile (tries by id, then dedicated routes). */
export async function saveMyCandidateProfile(payload, { candidateId } = {}) {
  const tryById = async id => {
    const { id: _omit, ...body } = payload;
    const r = await api.put(`/candidates/${id}`, body);
    return r.data;
  };

  const tryDedicated = async () => {
    try {
      const r = await api.put('/candidates/me', payload);
      return r.data;
    } catch (err) {
      const status = err.response?.status;
      if (status !== 404 && status !== 403) throw err;
    }
    try {
      const r = await api.put('/candidate-profile', payload);
      return r.data;
    } catch (err) {
      const status = err.response?.status;
      if (status !== 404 && status !== 403) throw err;
    }
    return null;
  };

  if (candidateId) {
    try {
      return await tryById(candidateId);
    } catch (err) {
      const status = err.response?.status;
      if (status !== 404 && status !== 403) throw err;
      const dedicated = await tryDedicated();
      if (dedicated) return dedicated;
      throw err;
    }
  }

  const dedicated = await tryDedicated();
  if (dedicated) return dedicated;

  const listRes = await api.get('/candidates');
  const row = Array.isArray(listRes.data) ? listRes.data[0] : null;
  if (row?.id) {
    return tryById(row.id);
  }

  const err = new Error(
    'Could not save profile. Stop the server (Ctrl+C), run npm run dev in the server folder, then try again.'
  );
  err.response = { data: { message: err.message }, status: 404 };
  throw err;
}

/** Resolve candidate id before save when the form was loaded without one. */
export async function resolveMyCandidateId() {
  try {
    const r = await api.get('/candidate-profile');
    if (r.data?.id) return r.data.id;
  } catch (err) {
    const status = err.response?.status;
    if (status && status !== 404 && status !== 403) throw err;
  }
  const listRes = await api.get('/candidates');
  const row = Array.isArray(listRes.data) ? listRes.data[0] : null;
  return row?.id || null;
}

/** Map API candidate row to form state (same shape as Candidates.jsx edit modal). */
export function candidateApiToForm(cand, user) {
  if (!cand) {
    const userPhone = normalizeIndianMobileDigits(user?.phone || '');
    return {
      id: undefined,
      userId: user?.id,
      name: user?.displayName || user?.name || '',
      email: user?.email || '',
      phone: userPhone,
      personal: {
        name: user?.displayName || user?.name || '',
        dob: '',
        gender: '',
        maritalStatus: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: 'India'
      },
      educational: [],
      professional: [],
      contact: {
        email: user?.email || '',
        phone: userPhone,
        linkedin: '',
        website: ''
      },
      skills: [],
      technicalSkills: [],
      currentSalary: '',
      expectedSalary: '',
      experience: '',
      preferredWorkLocations: [],
      location: '',
      availability: '',
      resumeLink: '',
      profileHeading: '',
      status: 'active',
      isFresher: false,
      preferredWorkingModes: [],
      contactChannels: ['phone', 'email'],
      photo: '',
      languagesKnown: [],
      areasOfExpertise: []
    };
  }

  const educational = Array.isArray(cand.educational)
    ? cand.educational
    : cand.educational && Object.values(cand.educational).some(v => v)
      ? [cand.educational]
      : [];
  const professional = normalizeProfessionalRows(
    Array.isArray(cand.professional)
      ? cand.professional
      : cand.professional && Object.values(cand.professional).some(v => v)
        ? [cand.professional]
        : []
  );

  const name =
    cand.personal?.name || cand.contact?.name || cand.displayName || user?.name || '';
  const email = cand.contact?.email || cand.displayEmail || user?.email || '';
  const phone = normalizeIndianMobileDigits(
    cand.contact?.phone || cand.displayPhone || user?.phone || ''
  );

  return {
    id: cand.id,
    userId: cand.userId,
    name,
    email,
    phone,
    personal: cand.personal || {
      name,
      dob: '',
      maritalStatus: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'India'
    },
    educational,
    professional,
    contact: {
      ...(cand.contact || {}),
      email,
      phone,
      linkedin: cand.contact?.linkedin || '',
      website: cand.contact?.website || ''
    },
    skills: cand.skills || [],
    technicalSkills: cand.technicalSkills || [],
    currentSalary: cand.currentSalary || cand.salaryFrom || '',
    expectedSalary: cand.expectedSalary || cand.salaryTo || '',
    experience:
      cand.experience ??
      (cand.experienceYears != null ? String(cand.experienceYears) : ''),
    preferredWorkLocations: normalizePreferredWorkLocations(cand),
    location:
      preferredWorkLocationsToLegacyLocation(normalizePreferredWorkLocations(cand)) ||
      cand.location ||
      cand.displayLocation ||
      '',
    availability: cand.availability || '',
    resumeLink: cand.resumeLink || '',
    resumeOriginalName: cand.resumeOriginalName || cand.resumeFileName || '',
    resumeText: cand.resumeText || '',
    profileHeading: cand.profileHeading || '',
    status: cand.status || 'active',
    isFresher: !!cand.isFresher,
    preferredWorkingModes: cand.preferredWorkingModes || [],
    contactChannels: cand.contactChannels?.length ? cand.contactChannels : ['phone', 'email'],
    photo: cand.photo || '',
    languagesKnown: normalizeLanguagesKnown(cand.languagesKnown),
    areasOfExpertise: Array.isArray(cand.areasOfExpertise) ? cand.areasOfExpertise : [],
    profileCompletionPercent:
      typeof cand.profileCompletionPercent === 'number' ? cand.profileCompletionPercent : undefined
  };
}

/** Blank profile for the portal when the API has not returned a row yet. */
export function emptyCandidateProfileForm(user) {
  return candidateApiToForm(null, user);
}

export function normalizeCandidateForm(data, user) {
  if (!data) return null;
  const educational = Array.isArray(data.educational)
    ? data.educational
    : data.educational && Object.values(data.educational).some(v => v)
      ? [data.educational]
      : [];
  const professional = Array.isArray(data.professional)
    ? data.professional
    : data.professional && Object.values(data.professional).some(v => v)
      ? [data.professional]
      : [];

  return {
    id: data.id,
    userId: data.userId,
    personal: data.personal || {
      name: user?.displayName || user?.name || '',
      dob: '',
      maritalStatus: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'India'
    },
    educational,
    professional,
    contact: data.contact || {
      email: user?.email || '',
      phone: user?.phone || '',
      linkedin: '',
      website: ''
    },
    skills: data.skills || [],
    currentSalary: data.currentSalary ?? data.salaryFrom ?? '',
    expectedSalary: data.expectedSalary ?? data.salaryTo ?? '',
    experienceFrom: data.experienceFrom ?? '',
    experienceTo: data.experienceTo ?? '',
    experience:
      data.experience ??
      (data.experienceYears != null ? String(data.experienceYears) : ''),
    preferredWorkLocations: normalizePreferredWorkLocations(data),
    location:
      preferredWorkLocationsToLegacyLocation(normalizePreferredWorkLocations(data)) ||
      data.location ||
      '',
    availability: data.availability || '',
    preferredWorkingModes: data.preferredWorkingModes || [],
    contactChannels: data.contactChannels?.length ? data.contactChannels : ['phone', 'email'],
    isFresher: !!data.isFresher,
    resumeLink: data.resumeLink || '',
    resumeOriginalName: data.resumeOriginalName || data.resumeFileName || '',
    profileHeading: data.profileHeading || '',
    photo: data.photo || '',
    status: data.status || 'active',
    languagesKnown: normalizeLanguagesKnown(data.languagesKnown)
  };
}

/** Staff edit modal form state from API candidate row. */
export function staffCandidateFormFromApi(cand) {
  if (!cand) return { ...EMPTY_CANDIDATE_FORM };
  const base = candidateApiToForm(cand, null);
  return {
    ...EMPTY_CANDIDATE_FORM,
    ...base,
    personal: {
      ...EMPTY_CANDIDATE_FORM.personal,
      ...(base.personal || {}),
      name: base.personal?.name || base.name || ''
    },
    contact: { ...EMPTY_CANDIDATE_FORM.contact, ...(base.contact || {}) },
    password: 'password123',
    photoPreview: null,
    photoFile: null,
    resumeFile: null
  };
}
